import React, { useEffect, useState } from 'react';

import { Box, Button, Flex, Stack, Text } from '@mantine/core';
import { IconCode, IconPlus } from '@tabler/icons-react';
import { DroxyEmptyStateBox } from 'components/Droxy/DroxyEmptyStateBox';
import { useJsonSchemaBuilder } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/hooks/useJsonSchemaBuilder';
import { JsonPreviewPanel } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/JsonPreviewPanel';
import { ParameterFieldEditor } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/ParameterFieldEditor';
import { ParameterGroup } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/ParameterGroup';
import { useTranslation } from 'react-i18next';

interface JsonSchemaBuilderProps {
  value: string;
  onChange: (value: string) => void;
}

export function JsonSchemaBuilder({ value, onChange }: JsonSchemaBuilderProps) {
  const { t } = useTranslation();
  const {
    editingState,
    getSchema,
    getSchemaString,
    getPropertyAtSchemaPath,
    getExistingKeys,
    isPropertyRequired,
    startAddingProperty,
    startEditingProperty,
    cancelEditing,
    saveProperty,
    deleteProperty,
    toggleRequired
  } = useJsonSchemaBuilder(value);

  const [isPreviewVisible, setIsPreviewVisible] = useState(false);

  // Sync schema changes to parent component
  useEffect(() => {
    const currentSchemaString = getSchemaString();
    if (currentSchemaString !== value) {
      onChange(currentSchemaString);
    }
  }, [getSchemaString, value, onChange]);

  const currentSchema = getSchema();
  const hasAnyParameters = Object.keys(currentSchema.properties).length > 0;
  const isEditingActive = editingState.mode !== 'none';

  const parameterEditorComponent = isEditingActive ? (
    <ParameterFieldEditor
      mode={editingState.mode}
      path={editingState.path}
      getPropertyAtSchemaPath={getPropertyAtSchemaPath}
      getExistingKeys={getExistingKeys}
      isPropertyRequired={isPropertyRequired}
      onSave={saveProperty}
      onCancel={cancelEditing}
    />
  ) : null;

  const mainParametersContent =
    hasAnyParameters || editingState.mode === 'add' ? (
      <ParameterGroup
        properties={currentSchema.properties}
        required={currentSchema.required}
        path={[]}
        level={0}
        onEdit={startEditingProperty}
        onDelete={deleteProperty}
        onAddNested={startAddingProperty}
        onToggleRequired={toggleRequired}
        editingState={editingState}
        editorComponent={parameterEditorComponent}
      />
    ) : (
      <DroxyEmptyStateBox message={t('JSON_SCHEMA_BUILDER.NO_PARAMETERS_DEFINED')} />
    );

  const addParameterButton = !isEditingActive && (
    <Box
      mt="md"
      sx={(theme) => ({
        borderTop: hasAnyParameters ? `1px solid ${theme.other.gray.border}` : undefined,
        paddingTop: hasAnyParameters ? '16px' : undefined
      })}>
      <Button variant="filled" leftIcon={<IconPlus size={16} />} onClick={() => startAddingProperty()} size="sm">
        {t('JSON_SCHEMA_BUILDER.ADD_FIELD')}
      </Button>
    </Box>
  );

  const togglePreviewButton = (
    <Button
      variant="outline"
      leftIcon={<IconCode size={16} />}
      onClick={() => setIsPreviewVisible(!isPreviewVisible)}
      size="sm">
      {isPreviewVisible ? t('JSON_SCHEMA_BUILDER.HIDE_JSON_PREVIEW') : t('JSON_SCHEMA_BUILDER.SHOW_JSON_PREVIEW')}
    </Button>
  );

  return (
    <Stack spacing="md">
      <Flex justify="space-between" align="center">
        <Text fw={500} size="lg">
          {t('JSON_SCHEMA_BUILDER.DEFINE_PARAMETER_SCHEMA')}
        </Text>
        {togglePreviewButton}
      </Flex>

      {isPreviewVisible ? (
        <Flex gap="md" wrap="wrap">
          <Box style={{ flex: '1 1 400px', minWidth: 0 }}>
            {mainParametersContent}
            {addParameterButton}
          </Box>
          <Box style={{ flex: '0 1 400px', minWidth: 0 }}>
            <JsonPreviewPanel schema={currentSchema} />
          </Box>
        </Flex>
      ) : (
        <Stack spacing="md">
          {mainParametersContent}
          {addParameterButton}
        </Stack>
      )}
    </Stack>
  );
}
